
import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  BookOpen,
  ExternalLink,
  Fingerprint,
  GraduationCap,
  Radio,
  WifiOff,
  Zap
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PESAField } from './CasePage';

// ─────────────────────────────────────────────────────────────────────────────
// HOOK — Sync localStorage → état live
// ─────────────────────────────────────────────────────────────────────────────

const usePesaSync = (activeId, interval = 800) => {
  const [syncData, setSyncData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isStale, setIsStale] = useState(false);

  useEffect(() => {
    const tryLoad = () => {
      // Cherche par l'ID explicite, sinon auto-détecte
      let raw = activeId ? localStorage.getItem(`pesa_sync_${activeId}`) : null;

      if (!raw) {
        const keys = Object.keys(localStorage);
        const autoKey = keys.find(k => k.startsWith('pesa_sync_'));
        if (autoKey) raw = localStorage.getItem(autoKey);
      }

      if (!raw) return;
      try {
        const parsed = JSON.parse(raw);
        if (!parsed?.state) return;
        setSyncData(parsed);
        setLastUpdate(parsed.lastUpdate || Date.now());
        setIsStale(false);
      } catch { /* silent */ }
    };

    tryLoad();
    const tick = setInterval(() => {
      tryLoad();
      // Si dernière mise à jour > 8s → signal stale
      setIsStale(prev => {
        if (!lastUpdate) return prev;
        return Date.now() - lastUpdate > 8000;
      });
    }, interval);

    window.addEventListener('storage', tryLoad);
    return () => { clearInterval(tick); window.removeEventListener('storage', tryLoad); };
  }, [activeId, interval]);

  return { syncData, lastUpdate, isStale };
};

// ─────────────────────────────────────────────────────────────────────────────
// WAITING SCREEN (commun aux deux vues)
// ─────────────────────────────────────────────────────────────────────────────

const WaitingScreen = ({ label = 'Connexion au flux de données…' }) => (
  <div className="fixed inset-0 bg-[#050505] flex flex-col items-center justify-center gap-8 font-mono">
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
    >
      <Radio size={40} className="text-amber-500/60" />
    </motion.div>
    <div className="text-center space-y-2">
      <p className="text-amber-500 font-black uppercase tracking-[0.3em] text-xs animate-pulse">{label}</p>
      <p className="text-white/20 text-[9px] tracking-widest uppercase">
        Ouvrez le simulateur dans un autre onglet pour démarrer le flux
      </p>
    </div>
    {/* Debug */}
    <div className="absolute bottom-8 text-[8px] font-mono text-white/10 text-center">
      {Object.keys(localStorage).filter(k => k.startsWith('pesa_sync_')).join(' · ') || 'Aucune clé PESA détectée'}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// LIVE BADGE
// ─────────────────────────────────────────────────────────────────────────────

const LiveBadge = ({ isStale, title }) => (
  <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-2.5 rounded-full border backdrop-blur-xl bg-black/70 border-white/10">
    {isStale ? (
      <WifiOff size={12} className="text-red-400" />
    ) : (
      <span className="relative flex h-2.5 w-2.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
      </span>
    )}
    <p className={`font-black uppercase tracking-[0.2em] text-[10px] ${isStale ? 'text-red-400' : 'text-amber-400'}`}>
      {isStale ? 'Signal perdu' : `Flux Live — ${title || 'Simulation'}`}
    </p>
  </div>
);

// ═════════════════════════════════════════════════════════════════════════════
// FIELD CONFERENCE VIEW
// ═════════════════════════════════════════════════════════════════════════════

export const FieldConferenceView = () => {
  const params = useParams();
  const activeId = params.caseId || params.slug;
  const { syncData, isStale } = usePesaSync(activeId);

  // ── Overlay live metrics (coin bas droit)
  const LiveMetrics = ({ state }) => {
    if (!state?.parameters) return null;
    const params = state.parameters;
    const keys = Object.keys(params).slice(0, 6);

    return (
      <div className="absolute bottom-8 right-8 z-30 space-y-1.5 pointer-events-none">
        <p className="text-[8px] font-mono uppercase text-white/20 mb-2 tracking-widest text-right">
          Métriques live
        </p>
        {keys.map(k => {
          const v = params[k] ?? 0;
          const color = v >= 70 ? '#10b981' : v >= 40 ? '#f59e0b' : '#ef4444';
          return (
            <div key={k} className="flex items-center gap-3 justify-end">
              <span className="text-[8px] font-mono text-white/30 uppercase">{k}</span>
              <div className="w-16 h-1 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${v}%`, backgroundColor: color }} />
              </div>
              <span className="text-[9px] font-mono font-bold" style={{ color, minWidth: '2rem', textAlign: 'right' }}>
                {v}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Overlay step info (coin haut gauche, discret)
  const StepOverlay = ({ state, caseData }) => {
    const idx  = state?.currentStepIndex ?? 0;
    const step = caseData?.steps?.[idx];
    const phase = state?.currentPhase;

    if (!step) return null;
    return (
      <div className="absolute top-8 left-8 z-30 max-w-xs pointer-events-none">
        <div className="px-4 py-3 rounded-lg bg-black/60 border border-white/5 backdrop-blur-md space-y-1">
          <p className="text-[8px] font-mono uppercase text-white/20 tracking-widest">
            STEP {String(idx + 1).padStart(2, '0')} — {phase?.type?.toUpperCase() || 'INIT'}
          </p>
          <p className="text-sm font-semibold text-white leading-tight">{step.title}</p>
          {phase?.theoreticalHook && (
            <p className="text-[9px] text-indigo-300/60 italic leading-tight mt-1.5">
              {phase.theoreticalHook}
            </p>
          )}
        </div>
      </div>
    );
  };

  // ── Theory mode overlay (affiché si theoryMode actif dans le simulateur)
  const TheoryOverlay = ({ state, caseData }) => {
    if (!state?.theoryMode) return null;
    const theory = caseData?.initialState?.theory || {};
    if (!theory.primaryConcept) return null;

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="absolute left-8 bottom-8 z-30 max-w-sm pointer-events-none"
        >
          <div className="p-6 rounded-xl border border-amber-500/20 bg-black/80 backdrop-blur-xl space-y-3">
            <p className="text-[8px] font-mono uppercase text-amber-400/60 tracking-widest flex items-center gap-2">
              <BookOpen size={9} /> Cadre Théorique Actif
            </p>
            <div className="space-y-1">
              <p className="text-sm font-bold text-amber-400">{theory.primaryConcept}</p>
              <p className="text-[10px] text-white/50 italic">{theory.author} ({theory.year})</p>
            </div>
            {theory.quote && (
              <blockquote className="text-xs italic text-white/70 border-l border-amber-500/30 pl-3 leading-relaxed">
                "{theory.quote}"
              </blockquote>
            )}
            {/* Frameworks concurrents */}
            {theory.competingFrameworks?.slice(0, 3).map((fw, i) => (
              <div key={i} className="flex items-center gap-2 text-[9px] text-white/30">
                <span className="text-amber-400/40">vs.</span>
                <span>{fw.author} — {fw.concept}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
    );
  };

  if (!syncData) return <WaitingScreen />;

  const { state, case: caseData, theoryMode } = syncData;

  // Inject theoryMode in state for PESAField
  const enrichedState = { ...state, theoryMode };

  return (
    <div className="fixed inset-0 bg-black overflow-hidden">
      <PESAField
        externalState={enrichedState}
        isFullView={true}
      />

      <LiveBadge isStale={isStale} title={caseData?.title} />
      <StepOverlay state={state} caseData={caseData} />
      <LiveMetrics state={state} />
      <TheoryOverlay state={state} caseData={caseData} />

      {/* Timer si présent */}
      {state?.remainingTime != null && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 pointer-events-none">
          <div className="px-6 py-3 rounded-xl border border-amber-500/30 bg-black/70 backdrop-blur-md flex items-baseline gap-2">
            <span className="text-4xl font-bold font-mono text-amber-400">
              {String(state.remainingTime).padStart(2, '0')}
            </span>
            <span className="text-lg text-amber-500/60">S</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// THEORY CONFERENCE VIEW — Version refaite avec corpus corrigé
// ═════════════════════════════════════════════════════════════════════════════

// ── Helpers corpus
const buildCorpus = (caseData, state) => {
  const sources = [];

  // 1. Sources au niveau racine du case
  (caseData?.sources || []).forEach(s => sources.push({ ...s, _origin: 'case' }));

  // 2. Sources de la solution courante (debriefSources)
  const sol = state?.currentSolution;
  (sol?.interpretation?.debriefSources || []).forEach(s => sources.push({ ...s, _origin: 'solution' }));

  // 3. Si pas de solution courante, on cherche dans toutes les solutions
  if (!sol) {
    (caseData?.solutions || []).forEach(solution => {
      (solution?.interpretation?.debriefSources || []).forEach(s =>
        sources.push({ ...s, _origin: solution.name })
      );
    });
  }

  // 4. Frameworks de la théorie initiale → comme références secondaires
  const theory = caseData?.initialState?.theory || {};
  (theory.competingFrameworks || []).forEach(fw => {
    if (!sources.find(s => s.author === fw.author && s.title === fw.concept)) {
      sources.push({
        title: fw.concept,
        author: fw.author,
        year: fw.year || null,
        _origin: 'framework'
      });
    }
  });

  // Déduplique par title+author
  const seen = new Set();
  return sources.filter(s => {
    const key = `${s.title}|${s.author}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const buildVectors = (caseData, state) => {
  // Agents depuis state ET depuis la définition initiale (agentDefinitions)
  const pool = [
    ...(state?.agents || []),
    ...(caseData?.initialState?.agentDefinitions || []),
  ];

  return Array.from(
    new Map(
      pool
        .filter(a => a?.theory)
        .map(a => [a.name, a])
    ).values()
  ).filter(a => a.status !== 'hidden');
};

// ── Composant principal
export const TheoryConferenceView = () => {
  const params = useParams();
  const activeId = params.caseId || params.slug;
  const { syncData, isStale } = usePesaSync(activeId);

  const [openSections, setOpenSections] = useState({
    vectors: true,
    corpus: true,
    frameworks: false,
  });
  const toggle = key => setOpenSections(p => ({ ...p, [key]: !p[key] }));

  if (!syncData?.case) return <WaitingScreen label="Connexion au flux théorique…" />;

  const { state, case: caseData } = syncData;
  const theory      = caseData?.initialState?.theory || {};
  const sol         = state?.currentSolution;
  const interp      = sol?.interpretation || {};
  const vectors     = buildVectors(caseData, state);
  const corpus      = buildCorpus(caseData, state);
  const competing   = theory.competingFrameworks || [];

  const emancipationScore = state?.emancipationScore || state?.parameters
    ? Math.round(Object.values(state?.parameters || {}).reduce((s, v) => s + v, 0) / (Object.keys(state?.parameters || {}).length || 1))
    : 50;
  const theoreticalPromise = interp.theoreticalPromise ?? 50;
  const delta = emancipationScore - theoreticalPromise;

  const currentStep  = caseData?.steps?.[state?.currentStepIndex ?? 0];
  const currentPhase = state?.currentPhase;

  return (
    <div className="min-h-screen bg-[#050505] text-white font-serif overflow-y-auto">
      <LiveBadge isStale={isStale} title={caseData?.title} />

      <div className="max-w-6xl mx-auto px-8 py-16 pt-24 space-y-12">

        {/* ── HEADER */}
        <header className="space-y-4 border-b border-white/5 pb-10">
          <p className="text-[9px] font-sans font-black tracking-[0.3em] uppercase text-amber-500">
            PESA — Architecture Théorique / Session Live
          </p>
          <h1 className="text-4xl lg:text-6xl font-light italic tracking-tight leading-[1.1]">
            {caseData?.title || 'Analyse Systémique'}
          </h1>
          {theory.primaryConcept && (
            <div className="flex items-center gap-4 pt-2">
              <div className="px-4 py-2 border border-amber-500/30 bg-amber-500/5 text-amber-400 text-[10px] font-sans font-bold uppercase tracking-widest">
                {theory.primaryConcept}
              </div>
              <p className="text-sm font-sans text-white/40 italic">
                {theory.author}{theory.year ? `, ${theory.year}` : ''}
              </p>
            </div>
          )}
        </header>

        {/* ── DELTA SCORES */}
        <section className="grid grid-cols-3 gap-8 py-8 border-b border-white/5">
          <div>
            <p className="text-[9px] font-sans font-bold uppercase tracking-widest text-amber-500/60 mb-2">Observation réelle</p>
            <p className="text-5xl font-sans font-light">{emancipationScore.toFixed(1)}%</p>
          </div>
          <div className="flex flex-col items-center justify-center">
            <p className={`text-5xl font-sans font-black ${delta >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              Δ {delta > 0 ? '+' : ''}{delta.toFixed(1)}
            </p>
            <p className="text-[9px] font-sans uppercase tracking-widest text-white/30 mt-1">Écart de reproductibilité</p>
          </div>
          <div className="text-right">
            <p className="text-[9px] font-sans font-bold uppercase tracking-widest text-white/30 mb-2">Modèle théorique</p>
            <p className="text-5xl font-sans font-light text-white/30">{theoreticalPromise}%</p>
          </div>
        </section>

        {/* ── PHASE EN COURS (si timer actif) */}
        <AnimatePresence>
          {currentPhase && (
            <motion.section
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 border border-indigo-500/20 bg-indigo-500/5 rounded-xl space-y-3"
            >
              <div className="flex items-center gap-3">
                <Activity size={14} className="text-indigo-400" />
                <p className="text-[9px] font-sans font-black uppercase tracking-widest text-indigo-400">
                  Phase active — {currentPhase.type?.toUpperCase()}
                </p>
              </div>
              {currentPhase.title && (
                <p className="text-xl font-sans font-semibold text-white">{currentPhase.title}</p>
              )}
              {currentPhase.narrative && (
                <p className="text-sm font-sans text-white/60 leading-relaxed italic">{currentPhase.narrative}</p>
              )}
              {currentPhase.theoreticalHook && (
                <div className="pt-3 border-t border-indigo-500/15">
                  <p className="text-[9px] font-sans uppercase text-indigo-400/50 mb-1.5 tracking-widest">Ancrage théorique</p>
                  <p className="text-sm font-sans text-indigo-200/80 leading-relaxed italic">{currentPhase.theoreticalHook}</p>
                </div>
              )}
              {currentStep?.problematisation && (
                <div className="pt-3 border-t border-white/5">
                  <p className="text-[9px] font-sans uppercase text-white/20 mb-1.5 tracking-widest">Problématisation</p>
                  <p className="text-base font-serif text-white/80 italic">"{currentStep.problematisation}"</p>
                </div>
              )}
            </motion.section>
          )}
        </AnimatePresence>

        {/* ── CITATION PRINCIPALE */}
        {theory.quote && (
          <blockquote className="py-8 border-y border-white/5 text-2xl lg:text-3xl font-light italic leading-relaxed text-white/80 pl-6 border-l-4 border-amber-500">
            "{theory.quote}"
            <footer className="text-sm font-sans font-bold not-italic text-amber-500/60 mt-3">
              — {theory.author}{theory.year ? ` (${theory.year})` : ''}
            </footer>
          </blockquote>
        )}

        {/* ── KEY INSIGHT (solution courante) */}
        {interp.keyInsight && (
          <div className="p-6 border border-white/5 bg-white/[0.02] rounded-xl">
            <p className="text-[9px] font-sans font-black uppercase text-amber-500/60 mb-3 tracking-widest flex items-center gap-2">
              <Zap size={10} /> Insight moteur — {sol?.name}
            </p>
            <p className="text-lg leading-relaxed text-slate-300 italic">"{interp.keyInsight}"</p>
          </div>
        )}

        {/* ── VECTEURS CONCEPTUELS */}
        <div className="space-y-4">
          <button
            onClick={() => toggle('vectors')}
            className="flex items-center justify-between w-full group"
          >
            <div className="flex items-center gap-3">
              <Fingerprint className="text-amber-500" size={18} />
              <h3 className="text-sm font-sans font-black uppercase tracking-[0.15em]">
                Vecteurs Conceptuels Actifs
              </h3>
              <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 text-[9px] font-mono border border-amber-500/20">
                {vectors.length}
              </span>
            </div>
            <span className="text-white/20 text-xs font-mono">{openSections.vectors ? '▲' : '▼'}</span>
          </button>

          <AnimatePresence>
            {openSections.vectors && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {vectors.map((agent, i) => (
                    <div
                      key={i}
                      className="p-6 border-l-4 border-amber-500/50 bg-white/[0.02] space-y-3"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-[10px] font-sans font-black uppercase text-amber-500 tracking-widest">
                            {agent.theory.primaryConcept}
                          </p>
                          <p className="text-[9px] font-sans text-white/30 mt-0.5 italic">
                            {agent.displayName || agent.narrativeName || agent.name}
                            {agent.archetype && ` · ${agent.archetype}`}
                          </p>
                        </div>
                        <p className="text-[9px] font-sans text-white/20 font-bold uppercase italic shrink-0 ml-4">
                          {agent.theory.author}
                        </p>
                      </div>
                      <p className="text-base font-serif italic leading-relaxed text-white/85">
                        "{agent.theory.quote}"
                      </p>
                      {agent.theory.explanation && (
                        <p className="text-[11px] font-sans text-white/40 leading-relaxed">
                          {agent.theory.explanation}
                        </p>
                      )}
                      <p className="text-[9px] font-sans text-white/20 font-bold uppercase">
                        {agent.theory.author} ({agent.theory.year})
                      </p>
                    </div>
                  ))}
                  {vectors.length === 0 && (
                    <div className="col-span-2 text-center py-8 text-white/20 text-sm italic font-sans">
                      Aucun vecteur théorique identifié.
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── FRAMEWORKS CONCURRENTS */}
        {competing.length > 0 && (
          <div className="space-y-4">
            <button
              onClick={() => toggle('frameworks')}
              className="flex items-center justify-between w-full"
            >
              <div className="flex items-center gap-3">
                <GraduationCap className="text-slate-500" size={18} />
                <h3 className="text-sm font-sans font-black uppercase tracking-[0.15em] text-white/60">
                  Frameworks Concurrents
                </h3>
                <span className="px-2 py-0.5 rounded-full bg-slate-500/10 text-slate-400 text-[9px] font-mono border border-slate-500/20">
                  {competing.length}
                </span>
              </div>
              <span className="text-white/20 text-xs font-mono">{openSections.frameworks ? '▲' : '▼'}</span>
            </button>

            <AnimatePresence>
              {openSections.frameworks && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
                >
                  {competing.map((fw, i) => (
                    <div key={i} className="p-4 border border-white/5 bg-white/[0.02] space-y-2">
                      <p className="text-[10px] font-sans font-black uppercase tracking-tight text-white/70">
                        {fw.concept}
                      </p>
                      <p className="text-[10px] font-sans text-white/30 italic">
                        {fw.author}{fw.year ? ` (${fw.year})` : ''}
                      </p>
                      {fw.keyDifference && (
                        <p className="text-[9px] font-sans text-white/25 leading-relaxed">
                          {fw.keyDifference}
                        </p>
                      )}
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* ── CORPUS & RÉFÉRENCES */}
        <div className="space-y-4">
          <button
            onClick={() => toggle('corpus')}
            className="flex items-center justify-between w-full"
          >
            <div className="flex items-center gap-3">
              <BookOpen className="text-slate-400" size={18} />
              <h3 className="text-sm font-sans font-black uppercase tracking-[0.15em] text-white/60">
                Corpus & Références
              </h3>
              <span className="px-2 py-0.5 rounded-full bg-slate-500/10 text-slate-400 text-[9px] font-mono border border-slate-500/20">
                {corpus.length}
              </span>
            </div>
            <span className="text-white/20 text-xs font-mono">{openSections.corpus ? '▲' : '▼'}</span>
          </button>

          <AnimatePresence>
            {openSections.corpus && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                {corpus.length === 0 ? (
                  <p className="text-center py-8 text-white/20 text-sm italic font-sans">
                    Aucune référence dans le corpus.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {corpus.map((src, i) => (
                      <div key={i} className="flex items-start gap-4 p-4 border border-white/5 bg-white/[0.02]">
                        <span className="text-[9px] font-mono text-white/20 w-6 shrink-0 text-right">[{i + 1}]</span>
                        <div className="flex-1 min-w-0 space-y-1">
                          <p className="text-xs font-sans font-bold text-white/80 truncate">{src.title}</p>
                          <p className="text-[10px] font-sans text-white/40 italic">
                            {src.author}{src.year ? `, ${src.year}` : ''}
                          </p>
                          {src.keyPassage && (
                            <p className="text-[9px] font-sans text-white/25 italic leading-tight">{src.keyPassage}</p>
                          )}
                          {src._origin && src._origin !== 'case' && (
                            <span className="text-[8px] font-sans text-white/15 uppercase">[{src._origin}]</span>
                          )}
                        </div>
                        {src.url && (
                          <a href={src.url} target="_blank" rel="noreferrer" className="text-amber-400/40 hover:text-amber-400 transition-colors shrink-0">
                            <ExternalLink size={11} />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── FOOTER */}
        <footer className="pt-12 border-t border-white/5 flex justify-between items-center text-[9px] font-sans text-white/15 uppercase tracking-widest">
          <span>PESA Engine — Session Conférence</span>
          <span>{new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
        </footer>
      </div>
    </div>
  );
};